import { fireErrorAnalytics } from '@jira/platform__error-reporting/src/fireErrorAnalytics.tsx';
import { createFormatMessage } from '@jira/servicedesk__common/src/createFormatMessage.tsx';
import { getPageOnDelete } from '@jira/servicedesk__knowledge-common/src/fireErrorAnalyticsEvents.tsx';
import { deleteTopic as deleteTopicService } from '@jira/servicedesk__knowledge-common/src/deleteTopic.tsx';
import { useFlagsService } from '@atlassian/jira-flags';
import { fireTrackAnalytics } from '@atlassian/jira-product-analytics-bridge';

// eslint-disable-next-line jira/restricted/@atlassian/react-sweet-state
import type { StoreActionApi } from '@atlassian/react-sweet-state';

import { defineMessage } from '@atlassian/jira-intl';
import { TOPICS_PAGE_SIZE, PACKAGE_NAME, TEAM_NAME } from './teamName.constants.tsx';
import type { Props, State } from './SuggestedTopicState.types.tsx';
import { removeTopic } from './removeTopic.tsx';

const dismissTopicSuccessTitle = defineMessage({
	id: 'servicedesk-knowledge-hub-topics.controllers.store.actions.dismiss-topic.dismiss-topic-success-title',
	defaultMessage: 'Removed the suggested topic',
	description: 'The title text of the success flag for removing a topic.',
});

const dismissTopicErrorTitle = defineMessage({
	id: 'servicedesk-knowledge-hub-topics.controllers.store.actions.dismiss-topic.dismiss-topic-error-title',
	defaultMessage: 'We couldn’t remove the topic',
	description: 'There was an API failure and the action couldn’t be completed.',
});

const dismissTopicErrorDescription = defineMessage({
	id: 'servicedesk-knowledge-hub-topics.controllers.store.actions.dismiss-topic.dismiss-topic-error-description',
	defaultMessage: 'Something went wrong. Try again or refresh the page',
	description: 'There was an API failure and the action couldn’t be completed.',
});

export const dismissTopic =
	(topicId: string) =>
	async ({ getState, setState }: StoreActionApi<State>, props: Props): Promise<void> => {
		const { projectId, createAnalyticsEvent, locale, activationId } = props;
		const currentTopics = getState().topics.data;
		// eslint-disable-next-line jira/react/no-usage-of-hooks-outside-components
		const { showFlag } = useFlagsService();
		const formatMessage = createFormatMessage(locale);

		try {
			setState({
				...getState(),
				topics: {
					...getState().topics,
					loading: true,
					error: null,
				},
			});
			projectId && (await deleteTopicService(projectId, topicId, activationId));
			const updatedTopics = removeTopic(currentTopics, topicId);
			const page = getPageOnDelete(getState().page, updatedTopics.length, TOPICS_PAGE_SIZE);
			setState({
				...getState(),
				topics: {
					data: updatedTopics,
					loading: false,
					error: null,
				},
				page,
			});
			showFlag({
				type: 'success',
				title: formatMessage(dismissTopicSuccessTitle),
				isAutoDismiss: true,
				// eslint-disable-next-line jira/choreographer/message-id-by-folder-structure
				messageId: 'servicedesk-knowledge-performance.show-flag.success.dismiss-topic',
				messageType: 'transactional',
			});
			fireTrackAnalytics(createAnalyticsEvent({}), 'dismissTopic succeeded');
			// oxlint-disable-next-line @typescript-eslint/no-explicit-any
		} catch (error: any) {
			setState({
				...getState(),
				topics: {
					...getState().topics,
					loading: false,
					error,
				},
			});
			showFlag({
				type: 'error',
				title: formatMessage(dismissTopicErrorTitle),
				description: formatMessage(dismissTopicErrorDescription),
				isAutoDismiss: true,
				// eslint-disable-next-line jira/choreographer/message-id-by-folder-structure
				messageId: 'servicedesk-knowledge-performance.show-flag.error.dismiss-topic',
				messageType: 'transactional',
			});
			fireErrorAnalytics({
				meta: {
					id: 'dismissTopic',
					packageName: PACKAGE_NAME,
					teamName: TEAM_NAME,
				},
				error,
			});
		}
	};
